import { useMemo } from 'react';
import { PageLayout } from '@/components/shared/PageLayout';
import PasswordChanger from './components/PasswordChanger';
import { ThemeSelector } from '@/components/shared/ThemeSelector';
import { LanguageSelector } from '@/components/shared/LanguageSelector';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MODULE_CHIP, type ModuleAccent } from '@/lib/moduleAccent';
import { SectionLabel } from '@/components/ui/section-label';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import type { LucideIcon } from 'lucide-react';
import {
  BrainCircuit,
  LineChart,
  Scan,
  Settings2,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import type { UserModules } from '@/constants/modules';

const MODULE_CONFIG: Record<
  UserModules,
  { icon: LucideIcon; translationKey: string; accent: ModuleAccent }
> = {
  traceability: {
    icon: Scan,
    translationKey: 'profile.modules.chip.traceability',
    accent: 'traceability',
  },
  analytics: {
    icon: LineChart,
    translationKey: 'profile.modules.chip.analytics',
    accent: 'analytics',
  },
  'machine-learning': {
    icon: BrainCircuit,
    translationKey: 'profile.modules.chip.machineLearning',
    accent: 'ml',
  },
};

const isKnownModule = (module: string): module is UserModules => module in MODULE_CONFIG;

export function ProfilePage() {
  const { tokenService } = useAuth();
  const { t } = useTranslation();

  const email = tokenService?.getUserMail() ?? '';
  const modules = useMemo(() => (tokenService?.getModules() ?? []) as string[], [tokenService]);

  const displayName = useMemo(() => {
    if (!email) return t('profile.hero.anonymous');
    const localPart = email.split('@')[0] ?? '';
    const normalized = localPart
      .replace(/[._-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
    return normalized || t('profile.hero.anonymous');
  }, [email, t]);

  const initials = useMemo(() => {
    const tokens = displayName
      .split(' ')
      .filter(Boolean)
      .map((token) => token.charAt(0).toUpperCase())
      .filter(Boolean);
    if (tokens.length === 0) return 'TL';
    return tokens.slice(0, 2).join('');
  }, [displayName]);

  const moduleBadges = useMemo(() => {
    const uniqueModules = Array.from(new Set(modules)).filter(isKnownModule);
    return uniqueModules.map((module) => {
      const config = MODULE_CONFIG[module];
      return { key: module, Icon: config.icon, label: t(config.translationKey), accent: config.accent };
    });
  }, [modules, t]);

  const emailDisplay = email || t('profile.overview.emailFallback');

  return (
    <PageLayout contentClassName="max-w-4xl">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">

        {/* ── Left: identity card ── */}
        <Card padding="none" className="flex flex-col overflow-hidden">
          {/* top band */}
          <div className="flex flex-col items-center gap-3 border-b border-primary/20 bg-primary/10 px-6 py-6 text-center">
            <SectionLabel icon={Sparkles} className="self-center">
              {t('profile.hero.badge')}
            </SectionLabel>
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-primary/30 bg-primary/15 text-2xl font-bold text-primary">
              {initials}
            </span>
            <p className="section-title leading-tight">{displayName}</p>
          </div>

          {/* body */}
          <div className="flex flex-col gap-4 p-5">
            {/* email */}
            <div>
              <p className="eyebrow">{t('profile.overview.emailLabel')}</p>
              <p className="mt-1 font-mono text-sm font-medium text-foreground break-all">
                {emailDisplay}
              </p>
            </div>

            <div className="h-px bg-border" />

            {/* modules */}
            <div>
              <div className="flex items-center gap-2">
                <p className="eyebrow">{t('profile.overview.modulesTitle')}</p>
                {moduleBadges.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {t('profile.overview.modulesCount', { count: moduleBadges.length })}
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('profile.overview.modulesHelper')}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {moduleBadges.length > 0 ? (
                  moduleBadges.map(({ key, Icon, label, accent }) => (
                    <Badge key={key} className={MODULE_CHIP[accent]}>
                      <Icon className="h-3 w-3" aria-hidden />
                      {label}
                    </Badge>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground italic">
                    {t('profile.overview.modulesEmpty')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* ── Right: preferences + security ── */}
        <div className="flex flex-col gap-6">

          {/* Preferences card */}
          <Card>
            <div className="flex flex-col gap-1">
              <SectionLabel icon={Settings2}>{t('profile.preferences.title')}</SectionLabel>
              <p className="text-sm text-muted-foreground">{t('profile.preferences.description')}</p>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <ThemeSelector />
              <LanguageSelector />
            </div>
          </Card>

          {/* Security card */}
          <Card>
            <div className="flex flex-col gap-1">
              <SectionLabel icon={ShieldCheck}>{t('profile.security.title')}</SectionLabel>
              <p className="text-sm text-muted-foreground">{t('profile.security.description')}</p>
            </div>
            <div className="mt-4">
              <PasswordChanger />
            </div>
          </Card>

        </div>
      </div>
    </PageLayout>
  );
}
